import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  IconButton,
  Card
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FolderIcon from '@mui/icons-material/Folder';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArticleIcon from '@mui/icons-material/Article';
import TableChartIcon from '@mui/icons-material/TableChart';
import DescriptionIcon from '@mui/icons-material/Description';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { formatLocalDateTime } from '../../utils/dateUtils';

const FileListView = ({
  view,
  items,
  viewType, // 'myDrive', 'shared', 'trash'
  selectedItems,
  selectedItem,
  sortBy,
  sortOrder,
  loading,
  onSort,
  onItemClick,
  onItemDoubleClick,
  onContextMenu,
  onToggleFavorite,
  onRename,
  onDownload,
  onEmptyTrash,
  getDisplayName,
  formatFileSize,
  groupTrashItemsByDate
}) => {
  // Action을 한글로 매핑
  const getActionText = (action) => {
    const actionMap = {
      'insert': '업로드',
      'update': '수정',
      'rename': '이름 변경',
      'move': '이동',
      'softDelete': '삭제',
      'realDelete': '영구 삭제',
      'restore': '복원',
      'shared': '공유',
      'download': '다운로드',
      'copy': '복사',
      'bookmark': '즐겨찾기'
    };
    return actionMap[action] || action;
  };

  // 파일 아이콘을 반환하는 함수
  const getFileIcon = (extension, size = 'medium') => {
    const iconProps = { fontSize: size };

    // 폴더 - Google Drive 스타일 회색 폴더
    if (!extension) return <FolderIcon {...iconProps} sx={{ color: '#9aa0a6' }} />;

    const ext = extension.toLowerCase();

    // 이미지 파일들 - 초록색
    if (ext === 'jpg' || ext === 'jpeg' || ext === 'png' ||
        ext === 'gif' || ext === 'bmp' || ext === 'svg' || ext === 'webp')
      return <ImageIcon {...iconProps} sx={{ color: '#34a853' }} />;

    // PDF - 빨간색
    if (ext === 'pdf')
      return <PictureAsPdfIcon {...iconProps} sx={{ color: '#ea4335' }} />;

    // Word 문서 - 파란색
    if (ext === 'doc' || ext === 'docx')
      return <ArticleIcon {...iconProps} sx={{ color: '#4285f4' }} />;

    // PowerPoint - 주황색
    if (ext === 'ppt' || ext === 'pptx')
      return <DescriptionIcon {...iconProps} sx={{ color: '#ff6d01' }} />;

    // Excel - 초록색
    if (ext === 'xls' || ext === 'xlsx' || ext === 'csv')
      return <TableChartIcon {...iconProps} sx={{ color: '#0f9d58' }} />;

    // 텍스트 파일 - 회색
    if (ext === 'txt' || ext === 'md' || ext === 'rtf')
      return <DescriptionIcon {...iconProps} sx={{ color: '#5f6368' }} />;

    // 압축 파일 - 보라색
    if (ext === 'zip' || ext === 'rar' || ext === '7z' ||
        ext === 'tar' || ext === 'gz')
      return <InsertDriveFileIcon {...iconProps} sx={{ color: '#9c27b0' }} />;

    // 비디오 파일 - 빨간색
    if (ext === 'mp4' || ext === 'avi' || ext === 'mov' ||
        ext === 'wmv' || ext === 'flv' || ext === 'webm')
      return <InsertDriveFileIcon {...iconProps} sx={{ color: '#f44336' }} />;

    // 오디오 파일 - 오렌지색
    if (ext === 'mp3' || ext === 'wav' || ext === 'flac' ||
        ext === 'aac' || ext === 'ogg')
      return <InsertDriveFileIcon {...iconProps} sx={{ color: '#ff9800' }} />;

    // 기본 파일 - 회색
    return <InsertDriveFileIcon {...iconProps} sx={{ color: '#5f6368' }} />;
  };
  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>로딩 중...</Typography>
      </Box>
    );
  }

  // 뷰 타입별 컬럼 구성 정의
  const getColumnConfig = () => {
    switch (viewType) {
      case 'myDrive':
        return {
          showOwner: false,
          showLocation: false,
          showDate: true,
          showSize: true,
          dateLabel: '마지막 수정일'
        };
      case 'shared':
        return {
          showOwner: true,
          showLocation: false,
          showDate: true,
          showSize: true,
          dateLabel: '마지막 수정일'
        };
      case 'trash':
        return {
          showOwner: true,
          showLocation: true,
          showDate: true,
          showSize: true,
          dateLabel: '삭제일'
        };
      case 'recent':
        return {
          showOwner: true,
          showLocation: false,
          showDate: true,
          showSize: true,
          showAction: true,  // 최근 문서함에만 action 컬럼 추가
          showUsingDate: true, // 최근 문서함에만 사용일 컬럼 추가
          dateLabel: '마지막 수정일'
        };
      case 'bookmarks':
        return {
          showOwner: true,
          showLocation: false,
          showDate: true,
          showSize: true,
          dateLabel: '마지막 수정일'
        };
      case 'storage':
        return {
          showOwner: false,
          showLocation: false,
          showDate: true,
          showSize: true,
          dateLabel: '마지막 수정일'
        };
      default:
        return {
          showOwner: true,
          showLocation: false,
          showDate: true,
          showSize: true,
          dateLabel: '마지막 수정일'
        };
    }
  };

  const columnConfig = getColumnConfig();

  if (items.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          {viewType === 'trash' ? '휴지통이 비어있습니다' : '폴더가 비어있습니다'}
        </Typography>
      </Box>
    );
  }

  if (view === 'grid') {
    return (
      <Box sx={{ p: 2 }}>
        {/* 폴더 섹션 */}
        {items.filter(item => !item.extension).length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#5f6368', fontSize: '16px' }}>
              폴더
            </Typography>
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, 240px)',
              gap: 2,
              justifyContent: 'start'
            }}>
              {items.filter(item => !item.extension).map((item) => (
                <Box key={item.uuid}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      position: 'relative',
                      height: 60,
                      width: 240,
                      backgroundColor: '#f8f9fa',
                      borderRadius: 3,
                      '&:hover': { 
                        boxShadow: 4,
                        '& .settings-icon': {
                          opacity: 1
                        }
                      },
                      ...(selectedItems.includes(item.uuid) && {
                        bgcolor: 'action.selected',
                        border: '2px solid #1976d2'
                      })
                    }}
                    onClick={(e) => {
                      // 우클릭은 무시 (컨텍스트 메뉴 간섭 방지)
                      if (e.button === 2) return;
                      onItemClick(item, e);
                    }}
                    onDoubleClick={() => onItemDoubleClick(item) }
                    onContextMenu={(e) => onContextMenu(e, item)}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      height: 60,
                      px: 2
                    }}>
                      {/* 좌측: 폴더 아이콘 + 폴더명 */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        flex: 1, 
                        overflow: 'hidden',
                        height: '100%'
                      }}>
                        {/* 폴더 아이콘 */}
                        <Box sx={{ 
                          mr: 1, 
                          flexShrink: 0, 
                          display: 'flex', 
                          alignItems: 'center',
                          height: '100%'
                        }}>
                          {getFileIcon(item.extension, 'small')}
                        </Box>

                        {/* 폴더명 */}
                        <Typography 
                          variant="body2" 
                          noWrap
                          sx={{ 
                            lineHeight: 1,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            alignItems: 'center',
                            height: '100%'
                          }}
                          title={getDisplayName(item)}
                        >
                          {getDisplayName(item)}
                        </Typography>
                      </Box>

                      {/* 우측: 설정 아이콘 */}
                      <IconButton
                        className="settings-icon"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onContextMenu(e, item);
                        }}
                        sx={{
                          opacity: 1,
                          ml: 0.5,
                          flexShrink: 0,
                          width: 24,
                          height: 24,
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Card>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* 파일 섹션 */}
        {items.filter(item => item.extension).length > 0 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: '#5f6368', fontSize: '16px' }}>
              파일
            </Typography>
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, 240px)',
              gap: 2,
              justifyContent: 'start'
            }}>
              {items.filter(item => item.extension).map((item) => (
                <Box key={item.uuid}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      position: 'relative',
                      height: 220,
                      width: 240,
                      backgroundColor: '#f8f9fa',
                      borderRadius: 3,
                      '&:hover': { 
                        boxShadow: 4,
                        '& .settings-icon': {
                          opacity: 1
                        }
                      },
                      ...(selectedItems.includes(item.uuid) && {
                        bgcolor: 'action.selected',
                        border: '2px solid #1976d2'
                      })
                    }}
                    onClick={(e) => {
                      // 우클릭은 무시 (컨텍스트 메뉴 간섭 방지)
                      if (e.button === 2) return;
                      onItemClick(item, e);
                    }}
                    onDoubleClick={() => onDownload(item) }
                    onContextMenu={(e) => onContextMenu(e, item)}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      height: '100%',
                      p: 1.5
                    }}>
                      {/* 상단: 아이콘 + 파일명 + 설정 아이콘 */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        height: '45px',
                        mb: 1.5
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          flex: 1, 
                          overflow: 'hidden',
                          pr: 0.5,
                          height: '100%'
                        }}>
                          {/* 파일 아이콘 */}
                          <Box sx={{ 
                            mr: 1, 
                            flexShrink: 0, 
                            display: 'flex', 
                            alignItems: 'center',
                            height: '100%'
                          }}>
                            {getFileIcon(item.extension, 'small')}
                          </Box>
                          
                          {/* 파일명.확장자 */}
                          <Typography 
                            variant="body2" 
                            noWrap
                            sx={{ 
                              lineHeight: 1,
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'flex',
                              alignItems: 'center',
                              height: '100%'
                            }}
                            title={getDisplayName(item)}
                          >
                            {getDisplayName(item)}
                          </Typography>
                        </Box>
                        
                        <IconButton
                          className="settings-icon"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onContextMenu(e, item);
                          }}
                          sx={{
                            opacity: 1,
                            ml: 0.5,
                            flexShrink: 0,
                            width: 24,
                            height: 24,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      {/* 하단: 파일 프리뷰 영역 */}
                      <Box sx={{ 
                        height: 170,
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        backgroundColor: '#ffffff',
                        borderRadius: 2,
                        overflow: 'hidden',
                        mx: 0,
                        mb: 0,
                        position: 'relative'
                      }}>
                        {item.thumbnailUrl ? (
                          <>
                            <Box
                              component="img"
                              src={item.thumbnailUrl}
                              alt={getDisplayName(item)}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextElementSibling.style.display = 'flex';
                              }}
                            />
                            <Box
                              sx={{
                                width: '100%',
                                height: '100%',
                                display: 'none',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'absolute',
                                top: 0,
                                left: 0
                              }}
                            >
                              {getFileIcon(item.extension, 'large')}
                            </Box>
                          </>
                        ) : (
                          getFileIcon(item.extension, 'large')
                        )}
                      </Box>
                    </Box>
                  </Card>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  // List view
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', px: 2 }}>
      {/* 휴지통 안내 메시지 */}
      {viewType === 'trash' && (
        <Box sx={{
          backgroundColor: '#e3f2fd',
          borderRadius: 2,
          p: 2,
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0
        }}>
          <Typography variant="body2" sx={{ color: '#000000' }}>
            휴지통에 있는 항목은 30일 후 완전 삭제됩니다.
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#1976d2', 
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:hover': {
                textDecoration: 'none'
              }
            }}
            onClick={() => {
              if (onEmptyTrash && window.confirm('휴지통을 비우시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
                onEmptyTrash();
              }
            }}
          >
            휴지통 비우기
          </Typography>
        </Box>
      )}

      <TableContainer component={Paper} sx={{ 
        backgroundColor: 'transparent', 
        boxShadow: 'none', 
        flex: 1,
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        }
      }}>
        <Table 
          stickyHeader 
          size="small" 
          sx={{ 
            tableLayout: 'auto',
            width: '100%'
          }}
        >
        <TableHead sx={{ 
          position: 'sticky', 
          top: 0, 
          zIndex: 1000,
          backgroundColor: '#ffffff'
        }}>
          <TableRow sx={{
            height: 36,
            '& .MuiTableCell-root': {
              paddingTop: '6px',
              paddingBottom: '6px',
              backgroundColor: '#ffffff !important',
              borderBottom: '1px solid #f0f0f0',
              whiteSpace: 'nowrap',
              position: 'sticky',
              top: 0,
              zIndex: 1001
            }
          }}>
            <TableCell sx={{ paddingLeft: 3, minWidth: '200px' }}>
              <TableSortLabel
                active={sortBy === 'name'}
                direction={sortBy === 'name' ? sortOrder : 'asc'}
                onClick={() => onSort('name')}
                sx={{ fontWeight: 500, color: '#5f6368' }}
              >
                이름
              </TableSortLabel>
            </TableCell>

            {/* 동작 컬럼 (최근 문서함만) - 이름 다음 */}
            {columnConfig.showAction && (
              <TableCell className="action-column" sx={{ 
                display: { xs: 'none', lg: 'table-cell' }, 
                width: '80px',
                minWidth: '70px'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#5f6368' }}>
                  동작
                </Typography>
              </TableCell>
            )}

            {/* 사용일 컬럼 (최근 문서함만) - 동작 다음 */}
            {columnConfig.showUsingDate && (
              <TableCell className="using-date-column" sx={{ 
                display: { xs: 'none', lg: 'table-cell' }, 
                width: '80px',
                minWidth: '70px'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#5f6368' }}>
                  사용일
                </Typography>
              </TableCell>
            )}
            
            {/* 소유자 컬럼 */}
            {columnConfig.showOwner && (
              <TableCell className="owner-column" sx={{ 
                display: { xs: 'none', lg: 'table-cell' }, 
                width: viewType === 'trash' ? '100px' : '120px',
                minWidth: '80px'
              }}>
                <TableSortLabel
                  active={sortBy === 'updateUser'}
                  direction={sortBy === 'updateUser' ? sortOrder : 'asc'}
                  onClick={() => onSort('updateUser')}
                  sx={{ fontWeight: 500, color: '#5f6368' }}
                >
                  소유자
                </TableSortLabel>
              </TableCell>
            )}
            
            {/* 원래 위치 컬럼 (휴지통에서만) */}
            {columnConfig.showLocation && (
              <TableCell className="location-column" sx={{ 
                display: { xs: 'none', md: 'table-cell' }, 
                width: '180px',
                minWidth: '120px',
                maxWidth: '250px' 
              }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#5f6368' }}>
                  원래 위치
                </Typography>
              </TableCell>
            )}
            
            {/* 날짜 컬럼 */}
            {columnConfig.showDate && (
              <TableCell className="date-column" sx={{ 
                display: { xs: 'none', lg: 'table-cell' }, 
                width: viewType === 'trash' ? '140px' : '160px',
                minWidth: '120px'
              }}>
                <TableSortLabel
                  active={sortBy === 'updateDate'}
                  direction={sortBy === 'updateDate' ? sortOrder : 'asc'}
                  onClick={() => onSort('updateDate')}
                  sx={{ fontWeight: 500, color: '#5f6368' }}
                >
                  {columnConfig.dateLabel}
                </TableSortLabel>
              </TableCell>
            )}
            
            {/* 파일 크기 컬럼 */}
            {columnConfig.showSize && (
              <TableCell className="size-column" sx={{ 
                display: { xs: 'none', xl: 'table-cell' }, 
                textAlign: 'right', 
                width: '100px',
                minWidth: '80px'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#5f6368' }}>
                  파일 크기
                </Typography>
              </TableCell>
            )}



            <TableCell sx={{ width: '120px', textAlign: 'center' }}>
              {/* 호버 액션 컬럼 - 헤더는 빈 공간 */}
            </TableCell>
            <TableCell sx={{ width: '50px', textAlign: 'center' }}>
              {/* 더보기 버튼 컬럼 */}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {viewType === 'trash' ? (
            (() => {
              const groupedItems = groupTrashItemsByDate(items);
              const dateLabels = {
                today: '오늘',
                yesterday: '어제',
                lastWeek: '지난주',
                lastMonth: '지난달',
                older: '그 이전'
              };
              
              return Object.entries(groupedItems).map(([dateKey, dateItems]) => {
                if (dateItems.length === 0) return null;
                
                return [
                  // 날짜 헤더 행
                  <TableRow key={`header-${dateKey}`}>
                    <TableCell 
                      colSpan={6 + (columnConfig.showAction ? 1 : 0) + (columnConfig.showUsingDate ? 1 : 0)} 
                      sx={{ 
                        fontWeight: 600,
                        color: '#5f6368',
                        fontSize: '14px',
                        py: 1.5,
                        border: 'none'
                      }}
                    >
                      {dateLabels[dateKey]}
                    </TableCell>
                  </TableRow>,
                  // 해당 날짜의 아이템들
                  ...dateItems.map((item) => (
                    <TableRow
                      key={item.uuid}
                      className="file-row"
                      hover
                      selected={selectedItems.includes(item.uuid)}
                      sx={{ 
                        cursor: 'pointer',
                        height: 36,
                        '&:hover': { 
                          backgroundColor: '#f8f9fa',
                          '& .hover-actions': {
                            opacity: 1
                          }
                        },
                        '&.Mui-selected': {
                          backgroundColor: '#e8f0fe'
                        },
                        borderBottom: '1px solid #f0f0f0',
                        '& .MuiTableCell-root': {
                          paddingTop: '4px',
                          paddingBottom: '4px'
                        }
                      }}
                      onClick={(e) => {
                      // 우클릭은 무시 (컨텍스트 메뉴 간섭 방지)
                      if (e.button === 2) return;
                      onItemClick(item, e);
                    }}
                      onDoubleClick={() => {
                        if (item.extension) {
                          // 파일인 경우 다운로드
                          onDownload(item);
                        } else {
                          // 폴더인 경우 기존 동작
                          onItemDoubleClick(item);
                        }
                      }}
                      onContextMenu={(e) => onContextMenu(e, item)}
                    >
                      <TableCell sx={{ paddingLeft: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {getFileIcon(item.extension, 'medium')}
                          <Typography 
                            variant="body1" 
                            noWrap
                            title={getDisplayName(item)}
                            sx={{ 
                              fontWeight: 400, 
                              color: '#202124',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {getDisplayName(item)}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* 동작 컬럼 (최근 문서함만) - 이름 다음 */}
                      {columnConfig.showAction && (
                        <TableCell className="action-column" sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ color: '#5f6368' }}>
                            {item.action ? getActionText(item.action) : '—'}
                          </Typography>
                        </TableCell>
                      )}

                      {/* 사용일 컬럼 (최근 문서함만) - 동작 다음 */}
                      {columnConfig.showUsingDate && (
                        <TableCell className="using-date-column" sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ color: '#5f6368' }}>
                            {item.usingDate ? new Date(item.usingDate).toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' }) : '—'}
                          </Typography>
                        </TableCell>
                      )}
                      
                      {/* 소유자 컬럼 */}
                      {columnConfig.showOwner && (
                        <TableCell className="owner-column" sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ color: '#5f6368' }}>
                              {item.createUserName || '나'}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      
                      {/* 원래 위치 컬럼 */}
                      {columnConfig.showLocation && (
                        <TableCell className="location-column" sx={{ 
                          display: { xs: 'none', md: 'table-cell' }, 
                          maxWidth: '250px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          <Typography variant="body2" sx={{ 
                            color: '#5f6368', 
                            fontSize: '0.85em',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {item.parentName || '내 드라이브'}
                          </Typography>
                        </TableCell>
                      )}
                      
                      {/* 날짜 컬럼 */}
                      {columnConfig.showDate && (
                        <TableCell className="date-column" sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ color: '#5f6368' }}>
                            {item.updateDate ? formatLocalDateTime(item.updateDate) : '-'}
                          </Typography>
                        </TableCell>
                      )}
                      
                      {/* 파일 크기 컬럼 */}
                      {columnConfig.showSize && (
                        <TableCell className="size-column" sx={{ display: { xs: 'none', xl: 'table-cell' }, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ color: '#5f6368' }}>
                            {item.size ? formatFileSize(item.size) : '—'}
                          </Typography>
                        </TableCell>
                      )}



                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box 
                          className="hover-actions"
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            gap: 0.5,
                            opacity: 0,
                            transition: 'opacity 0.2s ease-in-out',
                            '.file-row:hover &': {
                              opacity: 1
                            }
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (viewType !== 'trash') {
                                onToggleFavorite(item);
                              }
                            }}
                            disabled={viewType === 'trash'}
                            sx={{ 
                              width: 24,
                              height: 24,
                              '&:hover': { backgroundColor: viewType === 'trash' ? 'transparent' : '#f1f3f4' },
                              cursor: viewType === 'trash' ? 'default' : 'pointer'
                            }}
                          >
                            {item.bookmarkId ? (
                              <StarIcon fontSize="small" sx={{ color: viewType === 'trash' ? '#dadce0' : '#fbbc04' }} />
                            ) : (
                              <StarBorderIcon fontSize="small" sx={{ color: viewType === 'trash' ? '#dadce0' : '#5f6368' }} />
                            )}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (viewType !== 'trash' && selectedItems.length <= 1) {
                                onRename(item);
                              }
                            }}
                            disabled={viewType === 'trash' || selectedItems.length > 1}
                            sx={{ 
                              width: 24,
                              height: 24,
                              '&:hover': { backgroundColor: (viewType === 'trash' || selectedItems.length > 1) ? 'transparent' : '#f1f3f4' },
                              cursor: (viewType === 'trash' || selectedItems.length > 1) ? 'default' : 'pointer'
                            }}
                          >
                            <EditIcon fontSize="small" sx={{ color: (viewType === 'trash' || selectedItems.length > 1) ? '#dadce0' : '#5f6368' }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (item.extension) {
                                onDownload(item);
                              }
                            }}
                            sx={{ 
                              width: 24,
                              height: 24,
                              '&:hover': { backgroundColor: '#f1f3f4' }
                            }}
                            disabled={!item.extension}
                          >
                            <FileDownloadIcon fontSize="small" sx={{ color: item.extension ? '#5f6368' : '#dadce0' }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onContextMenu(e, item);
                          }}
                          sx={{ 
                            opacity: 0.6,
                            '&:hover': { opacity: 1, backgroundColor: '#f1f3f4' }
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ];
              }).flat().filter(Boolean);
            })()
          ) : (
            items.map((item) => (
              <TableRow
                key={item.uuid}
                className="file-row"
                hover
                selected={selectedItems.includes(item.uuid)}
                sx={{ 
                  cursor: 'pointer',
                  height: 36,
                  '&:hover': { 
                    backgroundColor: '#f8f9fa',
                    '& .hover-actions': {
                      opacity: 1
                    }
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#e8f0fe'
                  },
                  borderBottom: '1px solid #f0f0f0',
                  '& .MuiTableCell-root': {
                    paddingTop: '4px',
                    paddingBottom: '4px'
                  }
                }}
                onClick={(e) => {
                  // 우클릭은 무시 (컨텍스트 메뉴 간섭 방지)
                  if (e.button === 2) return;
                  onItemClick(item, e);
                }}
                onDoubleClick={() => {
                  if (item.extension) {
                    // 파일인 경우 다운로드
                    onDownload(item);
                  } else {
                    // 폴더인 경우 기존 동작
                    onItemDoubleClick(item);
                  }
                }}
                onContextMenu={(e) => onContextMenu(e, item)}
              >
                <TableCell sx={{ paddingLeft: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {getFileIcon(item.extension, 'medium')}
                    <Typography 
                      variant="body1" 
                      noWrap
                      title={getDisplayName(item)}
                      sx={{ 
                        fontWeight: 400, 
                        color: '#202124',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {getDisplayName(item)}
                    </Typography>
                  </Box>
                </TableCell>

                {/* 동작 컬럼 (최근 문서함만) - 이름 다음 */}
                {columnConfig.showAction && (
                  <TableCell className="action-column" sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" sx={{ color: '#5f6368' }}>
                      {item.action ? getActionText(item.action) : '—'}
                    </Typography>
                  </TableCell>
                )}

                {/* 사용일 컬럼 (최근 문서함만) - 동작 다음 */}
                {columnConfig.showUsingDate && (
                  <TableCell className="using-date-column" sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" sx={{ color: '#5f6368' }}>
                      {item.usingDate ? new Date(item.usingDate).toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' }) : '—'}
                    </Typography>
                  </TableCell>
                )}
                
                {/* 소유자 컬럼 */}
                {columnConfig.showOwner && (
                  <TableCell className="owner-column" sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ color: '#5f6368' }}>
                        {item.createUser || '나'}
                      </Typography>
                    </Box>
                  </TableCell>
                )}
                
                {/* 원래 위치 컬럼 */}
                {columnConfig.showLocation && (
                  <TableCell className="location-column" sx={{ 
                    display: { xs: 'none', md: 'table-cell' }, 
                    maxWidth: '250px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    <Typography variant="body2" sx={{ 
                      color: '#5f6368', 
                      fontSize: '0.85em',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {item.parentName || '내 드라이브'}
                    </Typography>
                  </TableCell>
                )}
                
                {/* 날짜 컬럼 */}
                {columnConfig.showDate && (
                  <TableCell className="date-column" sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" sx={{ color: '#5f6368' }}>
                      {item.updateDate ? formatLocalDateTime(item.updateDate) : '-'}
                    </Typography>
                  </TableCell>
                )}
                
                {/* 파일 크기 컬럼 */}
                {columnConfig.showSize && (
                  <TableCell className="size-column" sx={{ display: { xs: 'none', xl: 'table-cell' }, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" sx={{ color: '#5f6368' }}>
                      {item.size ? formatFileSize(item.size) : '—'}
                    </Typography>
                  </TableCell>
                )}



                <TableCell sx={{ textAlign: 'center' }}>
                  <Box 
                    className="hover-actions"
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      gap: 0.5,
                      opacity: 0,
                      transition: 'opacity 0.2s ease-in-out',
                      '.file-row:hover &': {
                        opacity: 1
                      }
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (viewType !== 'trash') {
                          onToggleFavorite(item);
                        }
                      }}
                      disabled={viewType === 'trash'}
                      sx={{ 
                        width: 24,
                        height: 24,
                        '&:hover': { backgroundColor: viewType === 'trash' ? 'transparent' : '#f1f3f4' },
                        cursor: viewType === 'trash' ? 'default' : 'pointer'
                      }}
                    >
                      {item.bookmarkId ? (
                        <StarIcon fontSize="small" sx={{ color: viewType === 'trash' ? '#dadce0' : '#fbbc04' }} />
                      ) : (
                        <StarBorderIcon fontSize="small" sx={{ color: viewType === 'trash' ? '#dadce0' : '#5f6368' }} />
                      )}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (viewType !== 'trash' && selectedItems.length <= 1) {
                          onRename(item);
                        }
                      }}
                      disabled={viewType === 'trash' || selectedItems.length > 1}
                      sx={{ 
                        width: 24,
                        height: 24,
                        '&:hover': { backgroundColor: (viewType === 'trash' || selectedItems.length > 1) ? 'transparent' : '#f1f3f4' },
                        cursor: (viewType === 'trash' || selectedItems.length > 1) ? 'default' : 'pointer'
                      }}
                    >
                      <EditIcon fontSize="small" sx={{ color: (viewType === 'trash' || selectedItems.length > 1) ? '#dadce0' : '#5f6368' }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.extension) {
                          onDownload(item);
                        }
                      }}
                      sx={{ 
                        width: 24,
                        height: 24,
                        '&:hover': { backgroundColor: '#f1f3f4' }
                      }}
                      disabled={!item.extension}
                    >
                      <FileDownloadIcon fontSize="small" sx={{ color: item.extension ? '#5f6368' : '#dadce0' }} />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onContextMenu(e, item);
                    }}
                    sx={{ 
                      opacity: 0.6,
                      '&:hover': { opacity: 1, backgroundColor: '#f1f3f4' }
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
    </Box>
  );
};

export default FileListView;